// src/features/arrivals/components/ArrivalsTable.tsx
//
// The attention-first arrivals table of mockup 01. Rows already arrive
// sorted server-side (attention first, then arrival time, then guest name —
// see wave2-api.md). Reuses CustomDatagrid via react-admin's `useList`
// (client wrapper around an already server-fetched page, same pattern as
// Reservas).
import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { CheckCircleOutlineRounded as AckIcon, CheckCircleRounded as AckedIcon, ClearRounded as ClearIcon, PushPinOutlined as PinIcon, PushPinRounded as PinnedIcon } from '@mui/icons-material';
import { FunctionField, ListContextProvider, useList, useTranslate } from 'react-admin';
import { es, enUS } from 'date-fns/locale';
import CustomDatagrid from '@/components/common/CustomDatagrid';
import { EmptyState } from '@/components/common';
import { StateChip } from '@/features/reservations/components/StateChip';
import {
  arrivalTime,
  displayIdentifier,
  formatIdentification,
  formatKey,
  formatPayment,
  formatPreCheckIn,
  formatStuckFlag,
} from '@/features/reservations/lib/reservationFormat';
import type { ReservationState } from '@/types/reservationState';

export interface ArrivalsTableProps {
  rows: ReservationState[];
  loading: boolean;
  onRowClick: (row: ReservationState) => void;
  onAcknowledge: (id: string) => void;
  onPin: (id: string, pinned: boolean) => void;
  /** "Solo pendientes" is on — the one filter this table's empty state can clear. */
  pendingOnly?: boolean;
  onClearPendingOnly?: () => void;
}

export const ArrivalsTable: React.FC<ArrivalsTableProps> = ({ rows, loading, onRowClick, onAcknowledge, onPin, pendingOnly, onClearPendingOnly }) => {
  const translate = useTranslate();
  const locale = translate('common.locale') === 'en' ? enUS : es;
  const listContext = useList({ data: rows, isLoading: loading, perPage: Math.max(rows.length, 1) });

  if (!loading && rows.length === 0) {
    return (
      <EmptyState
        message={translate('resources.reservations.emptyState.title')}
        description={translate('resources.arrivals.emptyState.description')}
        minHeight={160}
        showAction={Boolean(pendingOnly && onClearPendingOnly)}
        actionText={translate('resources.reservations.filters.clear')}
        actionIcon={<ClearIcon />}
        onAction={onClearPendingOnly}
      />
    );
  }

  return (
    <ListContextProvider value={listContext}>
      <CustomDatagrid resource="arrivals" rowClick={(_id, _resource, record) => { onRowClick(record as ReservationState); return false; }}>
        <FunctionField label={translate('resources.arrivals.columns.time')} render={(r: ReservationState) => arrivalTime(r, locale)} />
        <FunctionField
          label={translate('resources.arrivals.columns.guest')}
          render={(r: ReservationState) => (
            <Typography
              variant="body2"
              fontWeight={r.attention.pinned || r.live.needsHelp || r.live.stuck ? 700 : 400}
            >
              {displayIdentifier(r)}
            </Typography>
          )}
        />
        <FunctionField label={translate('resources.arrivals.columns.room')} render={(r: ReservationState) => r.roomNumber ?? '—'} />
        <FunctionField label={translate('resources.arrivals.columns.preCheckIn')} render={(r: ReservationState) => formatPreCheckIn(r.preCheckIn, translate, translate('common.locale'))} />
        <FunctionField label={translate('resources.arrivals.columns.payment')} render={(r: ReservationState) => formatPayment(r.payment, translate)} />
        <FunctionField label={translate('resources.arrivals.columns.identification')} render={(r: ReservationState) => formatIdentification(r.identification.identified, r.identification.total)} />
        <FunctionField
          label={translate('resources.arrivals.columns.state')}
          render={(r: ReservationState) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <StateChip state={r.state} />
              {/* The chip already says «Necesita ayuda» when that is the primary state. */}
              {r.live.needsHelp && r.state !== 'needsHelp' && (
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                  {translate('resources.arrivals.needsHelp')}
                </Typography>
              )}
              {!r.live.needsHelp && r.live.stuck && (
                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
                  ▲ {formatStuckFlag(r.live.minutesInSection, r.live.section, translate)}
                </Typography>
              )}
            </Box>
          )}
        />
        <FunctionField label={translate('resources.arrivals.columns.key')} render={(r: ReservationState) => formatKey(r.key, translate)} />
        <FunctionField
          label=""
          render={(r: ReservationState) => (
            <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title={translate(r.attention.acknowledged ? 'resources.arrivals.actions.acknowledged' : 'resources.arrivals.actions.acknowledge')}>
                <IconButton size="small" onClick={() => onAcknowledge(r.id)} disabled={r.attention.acknowledged}>
                  {r.attention.acknowledged ? <AckedIcon fontSize="small" color="success" /> : <AckIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title={translate(r.attention.pinned ? 'resources.arrivals.actions.unpin' : 'resources.arrivals.actions.pin')}>
                <IconButton size="small" onClick={() => onPin(r.id, !r.attention.pinned)}>
                  {r.attention.pinned ? <PinnedIcon fontSize="small" color="primary" /> : <PinIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        />
      </CustomDatagrid>
    </ListContextProvider>
  );
};

export default ArrivalsTable;
